/**
 * The manual, for the person applying.
 *
 * Written to be read by somebody who has never used the system and may be
 * having it read aloud to them. Each task is one short heading, a few plain
 * sentences, and a moving picture of the real screens doing that task. The
 * pictures are recordings of the deployed system, made by
 * `scripts/make-manual-gifs.mjs`, not drawings of it.
 */

import type { ReactElement } from "react";
import { bi, type Bilingual } from "@ksidc/ontology";
import { useLang } from "@ksidc/client";
import { DISCOVER, APPLY } from "../router";

interface Step {
  id: string;
  title: Bilingual;
  body: Bilingual[];
  gif?: string;
  caption?: Bilingual;
  cite?: { label: string; ref: string };
}

const STEPS: Step[] = [
  {
    id: "sign-in",
    title: bi("Signing in", "പ്രവേശിക്കുന്നത്"),
    body: [
      bi(
        "There is no password. Type your email address and press Send me a link. A link comes to that address. Open it and you are in.",
        "പാസ്‌വേഡ് ഇല്ല. നിങ്ങളുടെ ഇമെയിൽ വിലാസം എഴുതി, ലിങ്ക് അയയ്ക്കുക എന്ന ബട്ടൺ അമർത്തുക. ആ വിലാസത്തിലേക്ക് ഒരു ലിങ്ക് വരും. അത് തുറന്നാൽ നിങ്ങൾ അകത്ത് കയറി.",
      ),
      bi(
        "You can also press Continue with Google if you have a Google account. Use the same address every time, or you will not see your old applications.",
        "നിങ്ങൾക്ക് ഗൂഗിൾ അക്കൗണ്ട് ഉണ്ടെങ്കിൽ ഗൂഗിൾ ഉപയോഗിച്ച് തുടരുക എന്നതും അമർത്താം. എല്ലായ്പ്പോഴും ഒരേ വിലാസം തന്നെ ഉപയോഗിക്കുക, അല്ലെങ്കിൽ പഴയ അപേക്ഷകൾ കാണില്ല.",
      ),
    ],
  },
  {
    id: "find-what-you-need",
    title: bi("Finding out what you need", "എന്തൊക്കെ വേണം എന്ന് അറിയാൻ"),
    body: [
      bi(
        "Open What do I need. Answer four questions: which district the site is in, whether it is inside a notified industrial area, which area it is, and what the whole project will cost.",
        "എനിക്ക് എന്ത് വേണം എന്നത് തുറക്കുക. നാല് ചോദ്യങ്ങൾക്ക് ഉത്തരം നൽകുക: സ്ഥലം ഏത് ജില്ലയിലാണ്, അത് വിജ്ഞാപനം ചെയ്ത വ്യവസായ മേഖലയ്ക്കുള്ളിലാണോ, ഏത് മേഖലയാണ്, പദ്ധതിക്ക് ആകെ എത്ര ചെലവ് വരും.",
      ),
      bi(
        "The page then tells you which Board will decide your application and which section of the Act puts it there. It lists every clearance you need and the office that gives each one. You can do all this before you apply.",
        "അതിന് ശേഷം ഏത് ബോർഡാണ് അപേക്ഷ തീരുമാനിക്കുക എന്നും അത് നിയമത്തിലെ ഏത് വകുപ്പ് പ്രകാരമാണ് എന്നും പേജ് പറയും. വേണ്ട ഓരോ അനുമതിയും അത് നൽകുന്ന ഓഫീസും കാണിക്കും. അപേക്ഷിക്കുന്നതിന് മുൻപ് തന്നെ ഇതെല്ലാം ചെയ്യാം.",
      ),
    ],
    gif: "find-what-you-need",
    caption: bi(
      "Four questions, then the answer.",
      "നാല് ചോദ്യങ്ങൾ, പിന്നെ ഉത്തരം.",
    ),
    cite: { label: "Act", ref: "s.6, s.7, s.8, s.9" },
  },
  {
    id: "fill-the-form",
    title: bi("Filling in the form", "ഫോം പൂരിപ്പിക്കുന്നത്"),
    body: [
      bi(
        "There is one form for every clearance. It is Form A, the form printed in the government gazette, and it has fourteen parts. You fill it once instead of filling a different form at each office.",
        "എല്ലാ അനുമതികൾക്കും ഒറ്റ ഫോം. സർക്കാർ ഗസറ്റിൽ അച്ചടിച്ച ഫോം എ ആണ് ഇത്, അതിന് പതിനാല് ഭാഗങ്ങളുണ്ട്. ഓരോ ഓഫീസിലും വെവ്വേറെ ഫോം പൂരിപ്പിക്കുന്നതിന് പകരം ഒരു തവണ പൂരിപ്പിച്ചാൽ മതി.",
      ),
      bi(
        "Your answers are saved on your own phone or computer as you type. You can stop and come back later and nothing is lost. A star means the answer is required.",
        "നിങ്ങൾ ടൈപ്പ് ചെയ്യുമ്പോൾ തന്നെ ഉത്തരങ്ങൾ നിങ്ങളുടെ ഫോണിലോ കമ്പ്യൂട്ടറിലോ സൂക്ഷിക്കുന്നു. നിർത്തി പിന്നീട് തുടരാം, ഒന്നും നഷ്ടപ്പെടില്ല. നക്ഷത്രം ഉള്ളത് നിർബന്ധമായും നൽകേണ്ട ഉത്തരമാണ്.",
      ),
      bi(
        "In part thirteen you choose the clearances you want. That list is what the Board sends to the offices.",
        "പതിമൂന്നാം ഭാഗത്തിൽ നിങ്ങൾക്ക് വേണ്ട അനുമതികൾ തിരഞ്ഞെടുക്കുക. ആ പട്ടികയാണ് ബോർഡ് ഓഫീസുകൾക്ക് അയയ്ക്കുന്നത്.",
      ),
    ],
    gif: "fill-the-form",
    caption: bi("One form, one part at a time.", "ഒറ്റ ഫോം, ഒരു ഭാഗം വീതം."),
    cite: { label: "Act", ref: "r.5, Form A" },
  },
  {
    id: "see-where-it-is",
    title: bi("Seeing where your application is", "അപേക്ഷ എവിടെ എത്തി എന്ന് കാണാൻ"),
    body: [
      bi(
        "Open My applications and choose your application. One page answers the questions you used to have to visit an office to ask.",
        "എന്റെ അപേക്ഷകൾ തുറന്ന് നിങ്ങളുടെ അപേക്ഷ തിരഞ്ഞെടുക്കുക. ഓഫീസിൽ പോയി ചോദിക്കേണ്ടിയിരുന്ന കാര്യങ്ങൾക്ക് ഒരൊറ്റ പേജിൽ ഉത്തരം കിട്ടും.",
      ),
      bi(
        "It shows the day the Board received it, the last day the Board has to decide, how many days are still left, and the day a deemed clearance would arise. It also shows which offices have your file now and which have replied.",
        "ബോർഡിന് അപേക്ഷ കിട്ടിയ ദിവസം, തീരുമാനം എടുക്കാൻ ബോർഡിനുള്ള അവസാന ദിവസം, ഇനി എത്ര ദിവസം ബാക്കിയുണ്ട്, കൽപ്പിത അനുമതി ഉണ്ടാകുന്ന ദിവസം എന്നിവ കാണിക്കും. ഏതൊക്കെ ഓഫീസുകളുടെ കയ്യിലാണ് ഫയൽ ഇപ്പോൾ, ഏതൊക്കെ മറുപടി തന്നു എന്നും കാണിക്കും.",
      ),
      bi(
        "The Board does not act on the deemed date on its own. The date is worked out and shown to you because the law gives you that right. If a delay happens, report it to the grievance committee first.",
        "കൽപ്പിത തീയതിയിൽ ബോർഡ് സ്വയമേവ ഒന്നും ചെയ്യുന്നില്ല. നിയമം നിങ്ങൾക്ക് ആ അവകാശം നൽകുന്നതുകൊണ്ടാണ് ആ തീയതി കണക്കാക്കി കാണിക്കുന്നത്. കാലതാമസം ഉണ്ടായാൽ ആദ്യം പരാതി പരിഹാര സമിതിയെ അറിയിക്കുക.",
      ),
    ],
    gif: "see-where-it-is",
    caption: bi(
      "The dates, and who is holding the file.",
      "തീയതികളും ഫയൽ ആരുടെ കയ്യിലാണ് എന്നതും.",
    ),
    cite: { label: "Act", ref: "s.10, r.9(2)" },
  },
  {
    id: "the-site-visit",
    title: bi("The visit to your site", "നിങ്ങളുടെ സ്ഥലത്തേക്കുള്ള സന്ദർശനം"),
    body: [
      bi(
        "Before a decision is taken, somebody from the board office comes to see the site. The office asks you first when it would suit you. Open your application and offer a few times. Each one has to be at least a day away, so somebody can be sent.",
        "തീരുമാനത്തിന് മുൻപ് ബോർഡ് ഓഫീസിൽ നിന്ന് ഒരാൾ സ്ഥലം കാണാൻ വരും. എപ്പോഴാണ് സൗകര്യം എന്ന് ഓഫീസ് ആദ്യം നിങ്ങളോട് ചോദിക്കും. അപേക്ഷ തുറന്ന് ചില സമയങ്ങൾ നൽകുക. ആളെ അയക്കാൻ വേണ്ടി ഓരോന്നും ചുരുങ്ങിയത് ഒരു ദിവസം കഴിഞ്ഞായിരിക്കണം.",
      ),
      bi(
        "The office takes one of your times and tells you which. If you would rather not choose, that is fine: the office fixes a time and tells you beforehand. Either way you get the appointment as a calendar file, and Add to my calendar puts it in whatever calendar you already use.",
        "നിങ്ങൾ നൽകിയ ഒരു സമയം ഓഫീസ് എടുത്ത് ഏതെന്ന് അറിയിക്കും. നിങ്ങൾ തിരഞ്ഞെടുക്കാൻ താൽപ്പര്യമില്ലെങ്കിലും കുഴപ്പമില്ല: ഓഫീസ് സമയം നിശ്ചയിച്ച് മുൻകൂട്ടി അറിയിക്കും. ഏതായാലും കൂടിക്കാഴ്ച ഒരു കലണ്ടർ ഫയലായി കിട്ടും; എന്റെ കലണ്ടറിൽ ചേർക്കുക അമർത്തിയാൽ നിങ്ങൾ ഉപയോഗിക്കുന്ന കലണ്ടറിൽ അത് വരും.",
      ),
      bi(
        "You may be there yourself, or send somebody for you, or not be there at all. Not being there is not held against you. The rule asks the Board to tell you beforehand; it does not ask you to attend.",
        "നിങ്ങൾക്ക് നേരിട്ട് ഉണ്ടാകാം, ഒരാളെ അയക്കാം, അല്ലെങ്കിൽ ഇല്ലാതിരിക്കാം. ഇല്ലാതിരുന്നത് നിങ്ങൾക്ക് എതിരായി കണക്കാക്കില്ല. മുൻകൂട്ടി അറിയിക്കാനാണ് ചട്ടം ബോർഡിനോട് പറയുന്നത്; ഹാജരാകാൻ നിങ്ങളോട് പറയുന്നില്ല.",
      ),
      bi(
        "After the visit you can read what the officer wrote and see the photographs they took, on your own application page, in either language.",
        "സന്ദർശനത്തിന് ശേഷം ഉദ്യോഗസ്ഥൻ എഴുതിയത് വായിക്കാം, അവർ എടുത്ത ഫോട്ടോകൾ കാണാം; നിങ്ങളുടെ അപേക്ഷാ താളിൽ, ഏത് ഭാഷയിലും.",
      ),
    ],
    cite: { label: "Act", ref: "r.8" },
  },
  {
    id: "the-code-at-the-site",
    title: bi("The code we send while they are there", "അവർ അവിടെയുള്ളപ്പോൾ ഞങ്ങൾ അയക്കുന്ന കോഡ്"),
    body: [
      bi(
        "When the officer reaches the site, we send you a six figure code. Read it out to them and they type it in. That is all it does: it puts on the record that the officer really was at your site, in your presence, rather than the record simply taking their word for it.",
        "ഉദ്യോഗസ്ഥൻ സ്ഥലത്ത് എത്തുമ്പോൾ ഞങ്ങൾ നിങ്ങൾക്ക് ആറ് അക്ക കോഡ് അയക്കും. അത് അവർക്ക് വായിച്ചുകൊടുക്കുക, അവർ അത് ടൈപ്പ് ചെയ്യും. ഇത്രമാത്രമാണ് അതിന്റെ ജോലി: ഉദ്യോഗസ്ഥൻ നിങ്ങളുടെ സാന്നിധ്യത്തിൽ ശരിക്കും സ്ഥലത്തുണ്ടായിരുന്നു എന്ന് രേഖയിൽ വരും, അവരുടെ വാക്ക് വിശ്വസിക്കുകയല്ല.",
      ),
      bi(
        "You do not have to give it. If you are not there, or you would rather not, the visit goes ahead and is recorded exactly the same. Nothing about your application gets worse.",
        "അത് നൽകണമെന്ന് നിർബന്ധമില്ല. നിങ്ങൾ അവിടെയില്ലെങ്കിലും, അല്ലെങ്കിൽ താൽപ്പര്യമില്ലെങ്കിലും, സന്ദർശനം നടക്കുകയും അതേപടി രേഖപ്പെടുത്തുകയും ചെയ്യും. നിങ്ങളുടെ അപേക്ഷയ്ക്ക് ഒരു കുഴപ്പവും വരില്ല.",
      ),
      bi(
        "Read it out only to an officer standing in front of you at your site. Nobody from this office will ever telephone you and ask for it, and nobody will ever ask you for it over email or a message. It is never needed for a payment, and it is never needed to release your file. If anybody asks you for it any other way, do not give it.",
        "നിങ്ങളുടെ സ്ഥലത്ത് നിങ്ങളുടെ മുൻപിൽ നിൽക്കുന്ന ഉദ്യോഗസ്ഥനോട് മാത്രം അത് വായിച്ചുപറയുക. ഈ ഓഫീസിൽ നിന്ന് ആരും അതിനായി നിങ്ങളെ ഫോൺ ചെയ്യില്ല; ഇമെയിലിലൂടെയോ സന്ദേശത്തിലൂടെയോ ആരും ചോദിക്കില്ല. പണം അടയ്ക്കാൻ അത് ഒരിക്കലും വേണ്ട, ഫയൽ വിടാൻ അത് ഒരിക്കലും വേണ്ട. മറ്റേതെങ്കിലും വിധത്തിൽ ആരെങ്കിലും ചോദിച്ചാൽ നൽകരുത്.",
      ),
      bi(
        "The code stops working after about three quarters of an hour. If it runs out, the officer can have a fresh one sent to you.",
        "ഏകദേശം മുക്കാൽ മണിക്കൂർ കഴിഞ്ഞാൽ കോഡ് പ്രവർത്തിക്കില്ല. സമയം കഴിഞ്ഞാൽ പുതിയൊരെണ്ണം നിങ്ങൾക്ക് അയപ്പിക്കാൻ ഉദ്യോഗസ്ഥന് കഴിയും.",
      ),
    ],
    cite: { label: "Act", ref: "r.8" },
  },
  {
    id: "answer-a-question",
    title: bi("Answering a question from the Board", "ബോർഡിന്റെ ചോദ്യത്തിന് മറുപടി നൽകാൻ"),
    body: [
      bi(
        "If the Board needs something from you, a message appears on your application page. Until you answer, the days stop being counted. They start again on the day you answer.",
        "ബോർഡിന് നിങ്ങളിൽ നിന്ന് എന്തെങ്കിലും വേണമെങ്കിൽ അപേക്ഷാ പേജിൽ ഒരു സന്ദേശം വരും. നിങ്ങൾ മറുപടി നൽകുന്നത് വരെ ദിവസങ്ങൾ എണ്ണുന്നത് നിൽക്കും. മറുപടി നൽകുന്ന ദിവസം വീണ്ടും തുടങ്ങും.",
      ),
      bi(
        "Answer as soon as you can. The time the Board waits for you is time the Board does not have to decide.",
        "കഴിയുന്നത്ര വേഗം മറുപടി നൽകുക. ബോർഡ് നിങ്ങൾക്കായി കാത്തിരിക്കുന്ന സമയം തീരുമാനം എടുക്കാൻ ബോർഡിന് കിട്ടാത്ത സമയമാണ്.",
      ),
    ],
    cite: { label: "KSIDC", ref: "Reply sheet Q8" },
  },
  {
    id: "read-it-in-malayalam",
    title: bi("Reading it in Malayalam", "മലയാളത്തിൽ വായിക്കാൻ"),
    body: [
      bi(
        "Every screen is in English and Malayalam. Press മലയാളം at the top of any page and the whole page changes. Press English to change it back. Nothing else about your application changes.",
        "എല്ലാ സ്ക്രീനും ഇംഗ്ലീഷിലും മലയാളത്തിലും ഉണ്ട്. ഏത് പേജിന്റെയും മുകളിൽ മലയാളം അമർത്തിയാൽ പേജ് മുഴുവൻ മാറും. തിരിച്ച് മാറ്റാൻ English അമർത്തുക. അപേക്ഷയിൽ മറ്റൊന്നും മാറില്ല.",
      ),
    ],
    gif: "read-it-in-malayalam",
    caption: bi("One press changes the language.", "ഒറ്റ അമർത്തലിൽ ഭാഷ മാറും."),
  },
];

export function HelpScreen(): ReactElement {
  const { t } = useLang();

  return (
    <div className="stack">
      <h1>{t(bi("How to use this", "ഇത് എങ്ങനെ ഉപയോഗിക്കാം"))}</h1>
      <p className="lede">
        {t(
          bi(
            "Eight short things. Most have a moving picture of the real screens.",
            "എട്ട് ചെറിയ കാര്യങ്ങൾ. മിക്കതിനും യഥാർത്ഥ സ്ക്രീനുകളുടെ ചലിക്കുന്ന ചിത്രമുണ്ട്.",
          ),
        )}
      </p>

      <div className="notice notice-inline">
        <p>
          {t(
            bi(
              "The applications in the pictures are made up. They are there so the system can be shown working before real applications arrive.",
              "ചിത്രങ്ങളിലെ അപേക്ഷകൾ ഉണ്ടാക്കിയവയാണ്. യഥാർത്ഥ അപേക്ഷകൾ വരുന്നതിന് മുൻപ് സംവിധാനം പ്രവർത്തിക്കുന്നത് കാണിക്കാനാണ് അവ.",
            ),
          )}
        </p>
      </div>

      {STEPS.map((s, i) => (
        <section key={s.id} className="panel manual-step">
          <div className="eyebrow">
            {t(bi("Step", "ഘട്ടം"))} {i + 1}
          </div>
          <h2>{t(s.title)}</h2>
          {s.body.map((p, k) => (
            <p key={k}>{t(p)}</p>
          ))}
          {s.cite ? (
            <p className="prov-row">
              <span className="prov prov-statute">
                <b>{s.cite.label}</b>
                <span className="prov-ref">{s.cite.ref}</span>
              </span>
            </p>
          ) : null}
          {s.gif ? (
            <figure className="manual-figure">
              <img
                src={`/manual/${s.gif}.gif`}
                alt={t(s.caption ?? s.title)}
                loading="lazy"
                width={900}
                height={563}
              />
              {s.caption ? <figcaption>{t(s.caption)}</figcaption> : null}
            </figure>
          ) : null}
        </section>
      ))}

      <section className="panel">
        <h2>{t(bi("If something goes wrong", "എന്തെങ്കിലും കുഴപ്പം വന്നാൽ"))}</h2>
        <p>
          {t(
            bi(
              "If the system refuses something, it says which rule stopped it and what is missing. Read that line: it is telling you what to fix.",
              "സിസ്റ്റം എന്തെങ്കിലും നിരസിച്ചാൽ, ഏത് ചട്ടമാണ് തടഞ്ഞത് എന്നും എന്താണ് കുറവ് എന്നും അത് പറയും. ആ വരി വായിക്കുക: എന്ത് ശരിയാക്കണം എന്നാണ് അത് പറയുന്നത്.",
            ),
          )}
        </p>
        <div className="btn-row">
          <a className="btn" href={DISCOVER}>
            {t(bi("Find out what I need", "എനിക്ക് വേണ്ടത് കണ്ടെത്തുക"))}
          </a>
          <a className="btn btn-secondary" href={APPLY}>
            {t(bi("Go to the form", "ഫോമിലേക്ക് പോകുക"))}
          </a>
        </div>
      </section>
    </div>
  );
}
